import { useState, useRef } from 'react';
import { X, Plus, ChevronDown, ChevronUp, Upload, ImageIcon, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PlanFormData, PlanPhase } from '../types';

interface PlanFormModalProps {
    isOpen: boolean;
    isEditing: boolean;
    formData: PlanFormData;
    formLoading: boolean;
    onClose: () => void;
    onSubmit: () => void;
    onInputChange: (field: keyof PlanFormData, value: any) => void;
    onAddPhase: () => void;
    onUpdatePhase: (index: number, field: keyof Omit<PlanPhase, 'id'>, value: any) => void;
    onRemovePhase: (index: number) => void;
    onAddBenefit: () => void;
    onUpdateBenefit: (index: number, value: string) => void;
    onRemoveBenefit: (index: number) => void;
    onImageChange: (file: File | null, preview: string | null) => void;
}

export const PlanFormModal = ({
    isOpen,
    isEditing,
    formData,
    formLoading,
    onClose,
    onSubmit,
    onInputChange,
    onAddPhase,
    onUpdatePhase,
    onRemovePhase,
    onAddBenefit,
    onUpdateBenefit,
    onRemoveBenefit,
    onImageChange,
}: PlanFormModalProps) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [expandedSections, setExpandedSections] = useState({
        phases: true,
        benefits: true,
    });

    const toggleSection = (section: 'phases' | 'benefits') => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                onImageChange(file, reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        onImageChange(null, null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-800 rounded-md shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800 z-10">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {isEditing ? t('plan.form.editTitle') : t('plan.form.createTitle')}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Image Upload */}
                    <div>
                        <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">{t('plan.form.image')}</h4>
                        <div className="flex items-start gap-6">
                            {formData.image_preview ? (
                                <div className="relative group">
                                    <img
                                        src={formData.image_preview}
                                        alt="Preview"
                                        className="w-40 h-40 object-cover rounded-md border border-gray-200 dark:border-gray-700"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleRemoveImage}
                                        className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            ) : (
                                <div
                                    onClick={() => fileInputRef.current?.click()}
                                    className="w-40 h-40 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md cursor-pointer hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors"
                                >
                                    <ImageIcon className="h-8 w-8 text-gray-400 mb-2" />
                                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('plan.form.noImage')}</span>
                                </div>
                            )}
                            <div className="flex-1 space-y-3">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    className="hidden"
                                />
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                                >
                                    <Upload className="h-4 w-4 mr-2" />
                                    {formData.image_preview ? t('plan.form.changeImage') : t('plan.form.uploadImage')}
                                </button>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {t('plan.form.imageHelp')}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('plan.form.name')} <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                value={formData.name}
                                onChange={(e) => onInputChange('name', e.target.value)}
                                placeholder={t('plan.form.namePlaceholder')}
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('plan.form.description')}
                            </label>
                            <textarea
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                value={formData.description}
                                onChange={(e) => onInputChange('description', e.target.value)}
                                placeholder={t('plan.form.descriptionPlaceholder')}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('plan.form.status')}
                            </label>
                            <select
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                value={formData.is_active ? 'true' : 'false'}
                                onChange={(e) => onInputChange('is_active', e.target.value === 'true')}
                            >
                                <option value="true">{t('plan.statusActive')}</option>
                                <option value="false">{t('plan.statusInactive')}</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('plan.form.price')} <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                value={formData.price || ''}
                                onChange={(e) => onInputChange('price', Number(e.target.value))}
                                placeholder={t('plan.form.pricePlaceholder')}
                            />
                        </div>
                    </div>

                    {/* Profit Settings */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                        <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">{t('plan.form.profitSettings')}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t('plan.form.minProfitRate')}
                                </label>
                                <input
                                    type="number"
                                    step="0.1"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    value={formData.min_profit_rate || ''}
                                    onChange={(e) => onInputChange('min_profit_rate', Number(e.target.value))}
                                    placeholder="VD: 3"
                                />
                                <p className="text-xs text-gray-500 mt-1">{t('plan.form.minProfitHelp')}</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t('plan.form.highProfitRate')}
                                </label>
                                <input
                                    type="number"
                                    step="0.1"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    value={formData.high_profit_rate || ''}
                                    onChange={(e) => onInputChange('high_profit_rate', Number(e.target.value))}
                                    placeholder="VD: 4"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t('plan.form.highRateDuration')}
                                </label>
                                <input
                                    type="number"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    value={formData.high_rate_duration || ''}
                                    onChange={(e) => onInputChange('high_rate_duration', Number(e.target.value))}
                                    placeholder="VD: 3"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Duration & Commission Settings */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                        <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">{t('plan.form.durationCommission')}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t('plan.form.durationMonths')}
                                </label>
                                <input
                                    type="number"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    value={formData.duration_months || ''}
                                    onChange={(e) => onInputChange('duration_months', Number(e.target.value))}
                                    placeholder="VD: 36"
                                />
                                <p className="text-xs text-gray-500 mt-1">{t('plan.form.durationHelp')}</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t('plan.form.commissionF1')}
                                </label>
                                <input
                                    type="number"
                                    step="0.1"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    value={formData.commission_rate}
                                    onChange={(e) => onInputChange('commission_rate', Number(e.target.value))}
                                    placeholder="VD: 10"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t('plan.form.commissionStartMonth')}
                                </label>
                                <input
                                    type="number"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    value={formData.commission_start_month}
                                    onChange={(e) => onInputChange('commission_start_month', Number(e.target.value))}
                                    placeholder="VD: 1"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Slots & Tax */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                        <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">{t('plan.form.slotTax')}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t('plan.form.totalSlots')}
                                </label>
                                <input
                                    type="number"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    value={formData.total_slots}
                                    onChange={(e) => onInputChange('total_slots', Number(e.target.value))}
                                    placeholder="VD: 100"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t('plan.form.partPerUser')}
                                </label>
                                <input
                                    type="number"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    value={formData.part_per_user}
                                    onChange={(e) => onInputChange('part_per_user', Number(e.target.value))}
                                    placeholder="VD: 6"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t('plan.form.taxRate')}
                                </label>
                                <input
                                    type="number"
                                    step="0.1"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    value={formData.tax_rate}
                                    onChange={(e) => onInputChange('tax_rate', Number(e.target.value))}
                                    placeholder="VD: 10"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Phases Section */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                        <button
                            type="button"
                            onClick={() => toggleSection('phases')}
                            className="flex items-center justify-between w-full text-left"
                        >
                            <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                                {t('plan.form.phases')} ({formData.phases.length})
                            </h4>
                            {expandedSections.phases ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
                        </button>

                        {expandedSections.phases && (
                            <div className="mt-4 space-y-4">
                                {formData.phases.map((phase, index) => (
                                    <div key={index} className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-md border border-gray-200 dark:border-gray-700">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                {t('plan.form.phase')} {phase.phase_number}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => onRemovePhase(index)}
                                                className="text-red-500 hover:text-red-700 p-1"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                value={phase.title}
                                                onChange={(e) => onUpdatePhase(index, 'title', e.target.value)}
                                                placeholder={t('plan.form.phaseTitlePlaceholder')}
                                            />
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                value={phase.duration}
                                                onChange={(e) => onUpdatePhase(index, 'duration', e.target.value)}
                                                placeholder={t('plan.form.phaseDurationPlaceholder')}
                                            />
                                            <textarea
                                                rows={2}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white md:col-span-2"
                                                value={phase.description}
                                                onChange={(e) => onUpdatePhase(index, 'description', e.target.value)}
                                                placeholder={t('plan.form.phaseDescriptionPlaceholder')}
                                            />
                                        </div>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={onAddPhase}
                                    className="flex items-center justify-center w-full px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 hover:border-blue-500 hover:text-blue-500 transition-colors"
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    {t('plan.form.addPhase')}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Benefits Section */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                        <button
                            type="button"
                            onClick={() => toggleSection('benefits')}
                            className="flex items-center justify-between w-full text-left"
                        >
                            <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                                {t('plan.form.benefits')} ({formData.benefits.length})
                            </h4>
                            {expandedSections.benefits ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
                        </button>

                        {expandedSections.benefits && (
                            <div className="mt-4 space-y-3">
                                {formData.benefits.map((benefit, index) => (
                                    <div key={index} className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            value={benefit.description}
                                            onChange={(e) => onUpdateBenefit(index, e.target.value)}
                                            placeholder={t('plan.form.benefitPlaceholder')}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => onRemoveBenefit(index)}
                                            className="text-red-500 hover:text-red-700 p-2"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={onAddBenefit}
                                    className="flex items-center justify-center w-full px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 hover:border-blue-500 hover:text-blue-500 transition-colors"
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    {t('plan.form.addBenefit')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 sticky bottom-0 bg-white dark:bg-gray-800">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        {t('plan.form.cancel')}
                    </button>
                    <button
                        onClick={onSubmit}
                        disabled={formLoading}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                        {formLoading && (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        )}
                        {isEditing ? t('plan.form.update') : t('plan.form.create')}
                    </button>
                </div>
            </div>
        </div>
    );
};
